import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AuthenticationResult } from '../../services/AuthService';
import type { RootState } from './../store';

const ROLE_CLAIM_KEYS = [
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'role',
] as const;

const parseJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const base64 = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    );

    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const extractRolesFromPayload = (
  payload: Record<string, unknown>,
): string[] => {
  const roles = ROLE_CLAIM_KEYS.flatMap((claimKey) => {
    const value = payload[claimKey];

    if (typeof value === 'string') return [value];
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === 'string');
    }

    return [];
  });

  return Array.from(new Set(roles));
};

const resolveRoles = (
  token: string | null,
  fallbackRoles: string[] = [],
): string[] => {
  if (!token) return fallbackRoles;

  const payload = parseJwtPayload(token);
  if (!payload) return fallbackRoles;

  const tokenRoles = extractRolesFromPayload(payload);
  return tokenRoles.length > 0 ? tokenRoles : fallbackRoles;
};

export interface AuthState {
  userName: string;
  roles: string[];
  rememberMe: boolean;
  token: string | null;
  refreshToken: string | null;
}

export const initialState: AuthState = {
  userName: '',
  roles: [],
  rememberMe: false,
  token: null,
  refreshToken: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    setCredentials: (
      state: AuthState,
      action: PayloadAction<{
        response: AuthenticationResult;
        rememberMe: boolean;
      }>,
    ) => {
      if (action.payload.rememberMe === true) {
        const roles = resolveRoles(action.payload.response.token);

        state.userName = action.payload.response.userName;
        state.roles = roles;
        state.rememberMe = action.payload.rememberMe;
        state.token = action.payload.response.token;
        state.refreshToken = action.payload.response.refreshToken;
      }
    },
    logOut: (state, action: PayloadAction<void>) => {
      state.userName = '';
      state.roles = [];
      state.token = null;
      state.refreshToken = null;
      state.rememberMe = false;
    },
  },
});

export default authSlice.reducer;
export const { setCredentials, logOut } = authSlice.actions;

export const selectUser = (state: RootState) =>
  state.auth.rememberMe === true
    ? state.auth.userName
    : state.authSession.userName;

export const selectRememberMe = (state: RootState) =>
  state.auth.rememberMe === true;
const selectAuthRoles = (state: RootState) => state.auth.roles;
const selectAuthToken = (state: RootState) => state.auth.token;
const selectSessionRoles = (state: RootState) => state.authSession.roles;
const selectSessionToken = (state: RootState) => state.authSession.token;
const selectAuthRefreshToken = (state: RootState) => state.auth.refreshToken;
const selectSessionRefreshToken = (state: RootState) =>
  state.authSession.refreshToken;

export const selectRoles = createSelector(
  [
    selectRememberMe,
    selectAuthRoles,
    selectAuthToken,
    selectSessionRoles,
    selectSessionToken,
  ],
  (rememberMe, authRoles, authToken, sessionRoles, sessionToken) => {
    return rememberMe
      ? resolveRoles(authToken, authRoles)
      : resolveRoles(sessionToken, sessionRoles);
  },
);

export const selectToken = (state: RootState) =>
  state.auth.rememberMe === true ? state.auth.token : state.authSession.token;

export const selectRefreshToken = (state: RootState) =>
  state.auth.rememberMe === true
    ? selectAuthRefreshToken(state)
    : selectSessionRefreshToken(state);
